import React from 'react';
import {
  HelpCircle,
  BookOpen,
  MessageSquare,
  Globe,
  Github,
  Mail,
  FileQuestion,
  Lightbulb,
  Bug,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { isFeatureEnabled } from '@/config/features';

const Help: React.FC = () => {
  const resources = [
    {
      icon: BookOpen,
      title: 'Documentation',
      description: 'Comprehensive guides and API references',
      link: '#',
      color: 'bg-blue-100 text-blue-600',
    },
    {
      icon: MessageSquare,
      title: 'Community Forum',
      description: 'Get help from the community',
      link: '#',
      color: 'bg-green-100 text-green-600',
    },
    {
      icon: Github,
      title: 'GitHub Repository',
      description: 'Source code and issue tracking',
      link: 'https://github.com/dytafly/engine',
      color: 'bg-purple-100 text-purple-600',
    },
    {
      icon: Globe,
      title: 'Blog & Tutorials',
      description: 'Learn best practices and tips',
      link: '#',
      color: 'bg-yellow-100 text-yellow-600',
    },
  ];

  const faqs = [
    {
      question: 'How do I connect a new database?',
      answer: 'Navigate to the Connections page and click "Add Connection". Enter your database credentials and test the connection.',
    },
    {
      question: 'What databases are supported?',
      answer: 'We support PostgreSQL, MySQL, SQL Server, BigQuery, Snowflake, Databricks, ClickHouse, AWS Athena, DuckDB, SQLite, and Redshift.',
    },
    {
      question: 'How does the AI query generation work?',
      answer: isFeatureEnabled('GOLDEN_SQL')
        ? 'Our AI uses natural language processing to understand your intent and generates SQL based on your database schema and golden SQLs.'
        : 'Our AI uses natural language processing to understand your intent and generates SQL based on your database schema.',
    },
    ...(isFeatureEnabled('GOLDEN_SQL') ? [{
      question: 'Can I save and reuse queries?',
      answer: 'Yes! You can save successful queries as Golden SQLs to improve future query generation accuracy.',
    }] : []),
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="border-b border-[#e6e6ea] px-5 py-3 shrink-0">
        <h1 className="text-[18px] font-semibold text-[#22212d]">Help & Support</h1>
      </div>

      {/* Page Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-6">
        {/* Welcome Section */}
        <div className="mb-6">
          <h2 className="text-[24px] font-medium text-[#22212d] mb-1">
            How can we help you?
          </h2>
          <p className="text-[14px] text-[rgba(34,33,45,0.6)]">
            Find answers to your questions and get the help you need
          </p>
        </div>

        {/* Contact Support Alert */}
        <Alert className="mb-6 border-primary/20 bg-primary/5">
          <Lightbulb className="h-4 w-4 text-primary" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[14px] font-medium text-[#22212d]">Need immediate assistance?</p>
                <p className="text-[13px] text-[rgba(34,33,45,0.6)] mt-1">
                  Our support team is available Monday to Friday, 9 AM - 6 PM EST
                </p>
              </div>
              <Button variant="outline" size="sm" className="h-7 px-2.5 text-[13px] border-[#d8d8de]">
                <Mail className="mr-1.5 h-3.5 w-3.5" />
                Contact Support
              </Button>
            </div>
          </AlertDescription>
        </Alert>

      {/* Resources Grid */}
      <div className="mb-6">
        <h2 className="text-[18px] font-semibold text-[#22212d] mb-4">Resources</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {resources.map((resource, index) => {
            const Icon = resource.icon;
            return (
              <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer bg-white border-[#e6e6ea]">
                <CardContent className="p-5">
                  <div className={`p-3 rounded-lg ${resource.color} w-fit mb-3`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-[14px] font-semibold text-[#22212d] mb-2">{resource.title}</h3>
                  <p className="text-[13px] text-[rgba(34,33,45,0.6)] mb-3">
                    {resource.description}
                  </p>
                  <div className="flex items-center text-[13px] text-primary font-medium">
                    <span>Learn more</span>
                    <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* FAQs */}
      <Card className="mb-6 bg-white border-[#e6e6ea]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[16px] font-semibold text-[#22212d] flex items-center gap-2">
            <FileQuestion className="h-5 w-5" />
            Frequently Asked Questions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-[#e6e6ea] last:border-0 pb-4 last:pb-0">
                <h3 className="text-[14px] font-semibold text-[#22212d] mb-2">{faq.question}</h3>
                <p className="text-[13px] text-[rgba(34,33,45,0.6)]">{faq.answer}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Report Issue */}
      <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-white shadow-sm">
                <Bug className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-[16px] font-semibold text-[#22212d]">Found a bug?</h3>
                <p className="text-[13px] text-[rgba(34,33,45,0.6)]">
                  Help us improve by reporting issues you encounter
                </p>
              </div>
            </div>
            <Button variant="outline" className="h-7 px-2.5 text-[13px] border-[#d8d8de]">
              Report Issue
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default Help;